import api from "../utils/axios";

export const uploadDocument = async (formData) => {
  try {
    const response = await api.post("/documents/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getDocuments = async () => {
  try {
    const response = await api.get("/documents");
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getDocument = async (documentId) => {
  try {
    const response = await api.get(`/documents/${documentId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const updateDocument = async (id, data) => {
  try {
    const response = await api.put(`/documents/${id}`, data);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const deleteDocument = async (documentId) => {
  try {
    const response = await api.delete(`/documents/${documentId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getBulkUploadTemplates = async () => {
  try {
    const response = await api.get(`/documents/bulk-upload/templates`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getBulkUploadRequests = async ({
  page = 1,
  limit = 10,
  templateIds,
  statuses,
}) => {
  try {
    const params = {
      page,
      limit,
    };

    if (templateIds) {
      params.templateIds = templateIds;
    }

    if (statuses) {
      params.statuses = statuses;
    }

    const response = await api.get(`/documents/bulk-upload/requests`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const bulkSend = async (data) => {
  try {
    const response = await api.post(`/documents/bulk-upload/send`, data);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const createOnDemandSigningContainer = async (data) => {
  try {
    const response = await api.post(
      `/documents/ondemand-signing/containers`,
      data,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getOnDemandSigningContainers = async ({
  page = 1,
  limit = 10,
} = {}) => {
  try {
    const response = await api.get(`/documents/ondemand-signing/containers`, {
      params: { page, limit },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getOnDemandSigningContainerById = async (id) => {
  try {
    const response = await api.get(
      `/documents/ondemand-signing/containers/${id}`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getOnDemandSigningContainerRecipients = async (
  containerId,
  { page = 1, limit = 100, search = "", sortBy, sortOrder } = {},
) => {
  try {
    const params = { page, limit };
    if (search && String(search).trim()) {
      params.search = String(search).trim();
    }
    if (sortBy) {
      params.sortBy = sortBy;
    }
    if (sortOrder) {
      params.sortOrder = sortOrder;
    }
    const response = await api.get(
      `/documents/ondemand-signing/containers/${containerId}/recipients`,
      { params },
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const deleteOnDemandSigningContainer = async (id) => {
  try {
    const response = await api.delete(
      `/documents/ondemand-signing/containers/${id}`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

function safeDownloadBasename(name) {
  const raw = String(name || "container")
    .replace(/[^\w\-.]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
  return raw || "container";
}

function parseContentDispositionFilename(disposition) {
  if (!disposition || typeof disposition !== "string") return null;
  let m = /filename\*=(?:UTF-8''|utf-8'')([^;\n]+)/i.exec(disposition);
  if (m?.[1]) {
    try {
      return decodeURIComponent(m[1].trim().replace(/^["']|["']$/g, ""));
    } catch {
      return null;
    }
  }
  m = /filename="((?:[^"\\]|\\.)*)"/i.exec(disposition);
  if (m?.[1]) return m[1].replace(/\\"/g, '"');
  m = /filename=([^;\n]+)/i.exec(disposition);
  if (m?.[1]) return m[1].trim().replace(/^["']|["']$/g, "");
  return null;
}

export const exportOnDemandSigningContainerCsv = async (
  containerId,
  { containerName } = {},
) => {
  try {
    const response = await api.get(
      `/documents/ondemand-signing/containers/${containerId}/export`,
      { responseType: "blob" },
    );
    const contentType = response.headers["content-type"] || "";
    if (contentType.includes("application/json")) {
      const text = await response.data.text();
      const j = JSON.parse(text);
      throw new Error(j.message || "Export failed");
    }
    const disposition =
      response.headers["content-disposition"] ||
      response.headers["Content-Disposition"] ||
      "";
    const fromHeader = parseContentDispositionFilename(disposition);
    let filename = `${safeDownloadBasename(containerName)}.csv`;
    if (fromHeader) filename = fromHeader;
    const blob = new Blob([response.data], {
      type: "text/csv;charset=utf-8;",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    const data = error.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      let message = "Export failed";
      try {
        const j = JSON.parse(text);
        if (j.message) message = j.message;
      } catch {
        /* not JSON */
      }
      throw new Error(message);
    }
    const msg = error.response?.data?.message || error.message;
    throw new Error(typeof msg === "string" ? msg : "Export failed");
  }
};

export const queueOnDemandSignedPdfZipExport = async (containerId) => {
  try {
    const response = await api.post(
      `/documents/ondemand-signing/containers/${containerId}/export-signed-pdfs`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getOnDemandSignedPdfZipExportJob = async (jobId) => {
  try {
    const response = await api.get(`/documents/ondemand-signing/export-jobs/${jobId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getLatestCompletedOnDemandSignedPdfZipExport = async (containerId) => {
  try {
    const response = await api.get(
      `/documents/ondemand-signing/containers/${containerId}/export-signed-pdfs/latest`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getActiveOnDemandSignedPdfZipExport = async (containerId) => {
  try {
    const response = await api.get(
      `/documents/ondemand-signing/containers/${containerId}/export-signed-pdfs/active`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};
